import { Component, HostBinding, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DxDataGridComponent, DxFileUploaderComponent } from 'devextreme-angular';
import DataSource from 'devextreme/data/data_source';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { YEARS_LIST } from '@ag-common-lib/public-api';
import { BaseFormActions } from 'ag-common-svc/public-api';
import { DashboardReportingApiService } from 'ag-common-svc/lib/services/dashboard-reporting-api.service';
import { DashboardAgentPortalPaidUploadHistoryItemResponse } from 'ag-common-lib/lib/contracts/reporting-api/response/dashboard';
import { Constants } from 'ag-common-lib/lib/constants/validation.constants';
import { ModalWindowComponent } from 'ag-common-svc/lib/components/modal-window/modal-window.component';
import { ReportAdministrationFormService } from './report-administration-form.service';

@Component({
  selector: 'ag-crm-report-administration',
  templateUrl: './report-administration.component.html',
  styleUrls: ['./report-administration.component.scss'],
  providers: [ReportAdministrationFormService],
})
export class ReportAdministrationComponent implements OnInit, OnDestroy {
  @HostBinding('class') className = 'report-administration';
  @ViewChild('uploadsGrid', { static: false }) uploadsGridComponent: DxDataGridComponent;
  @ViewChild('fileUploader', { static: false }) fileUploaderComponent: DxFileUploaderComponent;
  @ViewChild('uploadPaidFileModalRef', { static: false }) uploadPaidFileModalComponent: ModalWindowComponent;

  protected readonly dateFormat = Constants.MMDDYYYY_DATE_SHORT_TIME_FORMAT;
  protected readonly dateOnlyFormat = Constants.DISPLAY_DATE_FORMAT;
  protected readonly numberFormat = { type: 'fixedPoint', precision: 0 };
  protected readonly years: number[] = YEARS_LIST;

  protected historyDataSource: DataSource;
  protected downloadingSourceFileId: number | null = null;

  private readonly destroyed$ = new Subject<void>();

  constructor(
    private readonly toastrService: ToastrService,
    private readonly dashboardReportingApiService: DashboardReportingApiService,
    protected readonly formService: ReportAdministrationFormService,
  ) {
    this.historyDataSource = this.dashboardReportingApiService.getAgentPortalPaidUploadHistoryDataSource();
  }

  ngOnInit(): void {
    this.formService.actions$.pipe(takeUntil(this.destroyed$)).subscribe(action => {
      if (action === BaseFormActions.created) {
        // Deliberate close after a successful import: bypass the discard/busy close guard
        // (import is still marked in-progress at this point) so the modal actually closes.
        void this.uploadPaidFileModalComponent?.forceCloseModal();
        this.uploadsGridComponent?.instance?.refresh();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  protected openUploadModal(): void {
    this.formService.initForm();
    this.fileUploaderComponent?.instance?.reset();
    this.uploadPaidFileModalComponent?.showModal();
  }

  protected handleUploadModalHidden = (): void => {
    this.fileUploaderComponent?.instance?.reset();
    this.formService.initForm();
  };

  protected clearSelectedFile(): void {
    this.formService.clearFile();
    this.fileUploaderComponent?.instance?.reset();
  }

  protected downloadSourceFile = async (
    historyItem: DashboardAgentPortalPaidUploadHistoryItemResponse,
  ): Promise<void> => {
    if (!historyItem?.id || this.downloadingSourceFileId === historyItem.id) {
      return;
    }

    this.downloadingSourceFileId = historyItem.id;

    try {
      const blob = await this.dashboardReportingApiService.downloadAgentPortalPaidFile(historyItem.id);
      this.downloadBlob(blob, historyItem.fileName || `agent-portal-paid-file-${historyItem.id}.csv`);
    } catch (error) {
      this.toastrService.error('Unable to download source file.', null, { disableTimeOut: true });
    } finally {
      this.downloadingSourceFileId = null;
    }
  };

  protected hasSourceFile = (historyItem: DashboardAgentPortalPaidUploadHistoryItemResponse): boolean => {
    return !!historyItem?.sourceFileKey;
  };

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.click();
    window.URL.revokeObjectURL(url);
  }
}
